import { Paths } from 'type-fest';
import { z } from 'zod';

import {
  LimitCreateMutationVariables,
  LimitFragment,
  LimitUpdateMutationVariables,
  OfferFragment,
} from '@occc/graphql';
import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';

import { ProgressPerPeriodFormValues } from './progress-per-period-limit';
import { ProgressPerTransactionFormValues } from './progress-per-transaction-limit';
import type { AwardCountLimitFormValue } from './award-count-limit';
import type { TransactionFrequencyLimitFormValue } from './transaction-frequency-limit';
import type { LimitSchema, TransactionLimitSchema } from './validation';

export enum LimitType {
  Activation = 'activation',
  AwardCount = 'awardCount',
}

export enum LimitScope {
  Membership = 'membership',
  Global = 'global',
  Transaction = 'transaction',
}

export enum LimitTimePeriod {
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
  Total = 'TOTAL',
}

export type LimitBaseInput = {
  limit: number;
  timePeriod?: LimitTimePeriod;
};

export type LimitInTime = LimitFragment & {
  timePeriod: LimitTimePeriod;
};

export type LimitFormValue = z.infer<typeof LimitSchema>;
export type TransactionLimitFormValue = z.infer<typeof TransactionLimitSchema>;

export type LimitsGroupFormValue = {
  membership: LimitFormValue;
  global: LimitFormValue;
  transaction?: Omit<LimitFormValue, 'timePeriod'>;
  progressPerPeriod?: ProgressPerPeriodFormValues;
  progressPerTransaction?: ProgressPerTransactionFormValues;
  transactionFrequency?: TransactionFrequencyLimitFormValue;
  membershipAwardCountLimits?: AwardCountLimitFormValue;
  globalAwardCountLimits?: AwardCountLimitFormValue;
};

export type LimitsInConstraintGroupFormValue = {
  isEnabled: boolean;
  membership: LimitFormValue;
  global: LimitFormValue;
  transaction?: Omit<LimitFormValue, 'timePeriod'>;
  progressPerPeriod?: ProgressPerPeriodFormValues;
  progressPerTransaction?: ProgressPerTransactionFormValues;
  transactionFrequency?: TransactionFrequencyLimitFormValue;
  membershipAwardCountLimits?: AwardCountLimitFormValue;
  globalAwardCountLimits?: AwardCountLimitFormValue;
};

export type LimitFormNamePaths = Paths<{
  awardCount: LimitsInConstraintGroupFormValue;
}>;

export type LimitComponentProps = {
  open: boolean;
  title?: string;
  description?: string;
  limitLabel?: string;
  showAvatar?: boolean;
};

export type GetLimitsOperations<T> = (
  offerId: string,
  values: T,
  dirtyFields: DirtyFieldsType,
  limitType: LimitType,
  options?: {
    isTransactionLimitSupported?: boolean; // When false, ensure no transaction limit: delete existing and skip processing
    isTransactionFrequencyLimitSupported?: boolean;
    offer?: OfferFragment; // Required when isTransactionFrequencyLimitSupported is true, to diff current limits
  },
) => Operation[];

export type MakeLimitOperations = (
  offerId: string,
  values: LimitsGroupFormValue,
  limitScope: LimitScope,
  limitType: LimitType,
  dirtyFields: DirtyFieldsType,
) => Operation[];

export type MakeCreateLimitOperation = (
  offerId: string,
  values: LimitBaseInput,
  limitType: LimitType,
  limitScope: LimitScope,
) => Operation<'limitCreate', LimitCreateMutationVariables>;

export type MakeUpdateLimitOperation = (
  id: string,
  values: LimitBaseInput,
  limitType: LimitType,
  limitScope: LimitScope,
) => Operation<'limitUpdate', LimitUpdateMutationVariables>;
